import { api } from '@/lib/api'
import { money, shortDate } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { PageHeader } from '@/components/ui/page'
import { Badge, Card, EmptyState, ErrorState, Loading } from '@/components/ui/primitives'
import { Table, Td, Th, Tr } from '@/components/ui/table'
import type { PortalDealsResponse, PortalQuotationStatus } from '@/types/api'

/**
 * The landing screen, because what a partner came to check first is whether the deal they
 * sourced actually closed.
 *
 * What is absent here is the design. No document, no line-level pricing, no cost figure,
 * no discount percentage, no customer contact and no approval history: a co-broke partner
 * is not the customer, and the offer is Codemax's to the customer. The API enforces that -
 * `PortalDeal` carries nothing else - and this screen renders the whole of it.
 */
export function Deals() {
  const deals = useAsync<PortalDealsResponse>(
    (signal) => api.get<PortalDealsResponse>('/portal/deals', undefined, signal),
    [],
  )

  if (deals.loading && !deals.data) return <Loading />
  if (deals.error) {
    return <ErrorState title={deals.error.title} detail={deals.error.detail} onRetry={deals.reload} />
  }

  const data = deals.data ?? { deals: [], totals: [] }

  return (
    <>
      <PageHeader
        title="Deals"
        hint="Quotations you sourced that the customer has signed. Value is net of discount and before tax."
      />

      {/* Two totals, never one. There are no FX rates in this system and the partner
          portal will not be where the first one is invented - ringgit and Singapore
          dollars sit side by side and stay there. */}
      {data.totals.length > 0 && (
        <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.totals.map((total) => (
            <Card key={total.currencyCode} className="px-5 py-4">
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-faint">
                {total.currencyCode}
              </p>
              <p className="tabular mt-1 text-xl font-semibold">
                {money(total.subtotal, total.currencyCode)}
              </p>
              <p className="mt-0.5 text-[12px] text-muted">
                {total.dealCount} {total.dealCount === 1 ? 'deal' : 'deals'}
              </p>
            </Card>
          ))}
        </div>
      )}

      <Card>
        {data.deals.length === 0 ? (
          <EmptyState
            title="No signed deals yet"
            hint="A deal appears here once the customer has signed it. Anything earlier is between Codemax and the customer."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Quotation</Th>
                <Th>Customer</Th>
                <Th>Country</Th>
                <Th>Issued</Th>
                <Th>Closed</Th>
                <Th>Status</Th>
                <Th numeric>Value</Th>
              </tr>
            </thead>
            <tbody>
              {data.deals.map((deal) => (
                <Tr key={deal.quotationId}>
                  <Td className="tabular whitespace-nowrap">{deal.quotationNo}</Td>
                  <Td>{deal.customerName}</Td>
                  <Td>{deal.countryIsoCode}</Td>
                  <Td className="whitespace-nowrap">{shortDate(deal.issueDate)}</Td>
                  <Td className="whitespace-nowrap">
                    {deal.closedAt ? (
                      shortDate(deal.closedAt)
                    ) : (
                      <span className="text-faint">&mdash;</span>
                    )}
                  </Td>
                  <Td>
                    <StatusBadge status={deal.status} />
                  </Td>
                  <Td numeric className="whitespace-nowrap">
                    {money(deal.subtotal, deal.currencyCode)}{' '}
                    <span className="text-[11px] text-faint">{deal.currencyCode}</span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  )
}

/**
 * Accepted appears beside Won, and they are not merged.
 *
 * Accepted means the customer has signed and Codemax has not yet recorded the internal
 * close. Hiding it would have a partner telephoning to ask about a deal we can see and
 * they cannot; merging the two would tell them a deal is won before anybody has said so.
 */
function StatusBadge({ status }: { status: PortalQuotationStatus }) {
  return status === 'Won' ? (
    <Badge tone="positive">Won</Badge>
  ) : (
    <Badge tone="accent">Accepted</Badge>
  )
}
